<template>
  <div class="base-input" :class="{ error: hasValidationError }">
    <label
      @click="() => !readonly && $refs.input.focus()"
      v-if="label"
      :class="['input__label', { readonly }]"
    >
      {{ label }}
      <span v-if="required">*</span>
      <VTooltip
        v-if="tooltip"
        :popup-text="tooltip"
        :show-tip="!!tooltip"
      >
        <svgicon
          class="svg-icon--input-info"
          icon="info"
          width="20"
          height="20"
        />
      </VTooltip>
    </label
    >
    <div class="input__field" v-if="readonly">
      {{ inputValue }}
    </div>
    <textarea
      autocomplete="off"
      ref="input"
      v-model="inputValue"
      @focus="focus"
      @blur="handleBlur"
      @input="handleInput"
      :name="name"
      :placeholder="placeholder"
      class="input__field input__field_textarea custom-scroll"
      :class="{ resizable }"
      :style="textareaStyle"
      :readonly="readonly"
      :disabled="disabled"
      :maxlength="limitMaxLength"
      v-else
    />
    <slot
      v-if="hasValidationError && !serverValidationPending"
      class="validation-messages"
    />
  </div>
</template>

<script>
import Base from './Base/Base.vue'
import VTooltip from '@/components/ui/VTooltip.vue'
import { mapState } from 'vuex'

export default {
  name: 'vtextarea',
  components: { VTooltip },
  extends: Base,
  data: () => ({
    textareaModalMaxHeight: ''
  }),
  mounted () {
    this.$nextTick(() => {
      this.resizeTextarea()
    })
  },
  computed: {
    ...mapState('mobile', {
      isMobile: 'isMobile'
    })
  },
  methods: {
    handleBlur () {
      this.$emit('blur')
      this.validate()
    },
    focus () {
      this.$refs.input.focus()
    },
    handleInput (e) {
      const el = e.target
      const sel = el.selectionStart

      this.resizeTextarea()
      this.$nextTick(() => {
        el.setSelectionRange(sel, sel)
      })
    },
    resizeTextarea () {
      const el = this.$refs.input
      if (!el) return

      // по умолчанию — обычная страница
      const DEFAULT_MAX = this.isMobile ? 300 : 560
      const MIN = 110

      // если пользователь растянул вручную выше 400 — не вмешиваемся
      if (this.resizable && el.offsetHeight > DEFAULT_MAX) return

      // если textarea внутри модалки — ограничиваем его максимально возможную высоту
      // Высчитываем размер окна и размер модалки без textarea, это и будет являться макс. высотой
      // Считаем один раз, когда нет textareaModalMaxHeight
      if (!this.textareaModalMaxHeight) {
        const modal = el.closest('.modal')

        if (modal) {
          const modalContent = modal.querySelector('.modal__content')
          if (modalContent) {
            const styles = getComputedStyle(modalContent)

            const marginTop = parseFloat(styles.marginTop) || 0
            const marginBottom = parseFloat(styles.marginBottom) || 0
            const currentHeight = el.offsetHeight || 0

            this.textareaModalMaxHeight =
              window.innerHeight -
              (modalContent.offsetHeight + marginTop + marginBottom - currentHeight)
          }
        }
      }

      // сброс перед перерасчётом
      el.style.height = 'auto'

      const maxHeight = this.textareaModalMaxHeight
        ? Math.min(this.textareaModalMaxHeight, DEFAULT_MAX)
        : DEFAULT_MAX

      const nextHeight = Math.max(
        MIN,
        Math.min(el.scrollHeight, maxHeight)
      )

      el.style.height = nextHeight + 'px'
    }
  }
}
</script>

<style lang="scss" scoped>
textarea {
  resize: none;
  overflow: auto;
  max-width: 100%;
  scrollbar-width: none;
  scrollbar-color: transparent;
  color: $color__input--font;
  font: inherit;
  border-radius: 8px;
  border: 1px solid $color__input--border;
  padding: 10px 15px;
  height: 110px;
  min-height: 110px;

  &.resizable {
    resize: vertical;
  }

  @media screen and (max-width: $tablet-width-s) {
    border-color: var(--color__pearl-dark-gray);
  }

  &::placeholder {
    letter-spacing: 0.01em;
    color: $color__input--font;
    font-size: 14px;
    font-weight: normal;
  }

  &:focus {
    border: 1px solid $color__input--focus;
    box-shadow: 0 0 0 2px $color__blue--light inset;
  }
}

.readonly-block {
  word-break: break-word;
  line-height: 150%;
}

// ::-webkit-scrollbar {
//   width: 0;
//   background: transparent;
// }
</style>
